
/**
 * mtuPlans holds all the "new" stripe billing plans
 * Everytime we add a new billing plan, add it to this list.
 *
 * 1 is the lowest tier. Free plans
 * 2 is the middle tier. Team plans
 * 3 is the highest tier. Business plans
 */

const mtuPlans = {
  developer: 0,
  'team-$100-1-month': 1,
  'team-$0': 1,
  business: 2
}

/**
 * features represents all the features in our app,
 * and their tier level, that we want to apply restrictions on
 */

const features = {
  schemaFiltering: 2,
  notifications: 2,
  warehouseScheduleSyncs: 2,
  warehouseSelectiveSyncs: 2,
  workspaceCollaborators: 2,
  automaticRedshiftVacuuming: 2
}

/**
 * featureCounts represents the feature and its limit
 * example: developer plans have a limit of 1 count per feature
 * whereas team plans have 7 owners, unlimited sources, & 1 warehouse
 */

const featureCounts = {
  developer: {
    owners: 1,
    sources: 1,
    warehouses: 1
  },
  'team-$100-1-month': {
    owners: 7,
    sources: Infinity,
    warehouses: 1
  },
  'team-$0': {
    owners: 7,
    sources: Infinity,
    warehouses: 1
  },
  business: {
    owners: Infinity,
    sources: Infinity,
    warehouses: Infinity
  }
}

/**
 * Restrict features for lower-tiered billing plans
 *
 * @param {String} feature the app feature we are checking the restriction for
 * @param {String} plan the stripe billing plan of this workspace
 * @return {Boolean}
 */

export function isFeatureAvailable (feature, plan) {
  if (!plan) throw new Error('must pass a plan arg')
  if (!feature) throw new Error('must pass a feature arg')
  if (typeof plan !== 'string') throw new Error('plan must be a string')
  if (typeof feature !== 'string') throw new Error('feature must be a string')
  if (!features[feature]) throw new Error('feature does not exist in billing plan utils')

  // only restrict features for MTU workspaces
  if (isMtuFreePlan(plan) || isMtuTeamPlan(plan) || isMtuBusinessPlan(plan)) {
    // first confirm that we have the plan listed in mtuPlans
    if (typeof mtuPlans[plan] !== 'number') throw new Error('plan does not exist in billing plan utils')
    return mtuPlans[plan] >= features[feature]
  }

  // all other plans receive no limitations for this feature
  return true
}

/**
 * Check if the workspace has hit their limit for this feature
 * example: this will return false if a team plan has 7 or more owners
 *
 * @param {String} plan the stripe billing plan of this workspace
 * @param {Number} ownerCount the number of owners currently on this workspace
 * @return {Boolean}
 */

export function isWithinFeatureLimit (feature, plan, count) {
  if (!feature) throw new Error('must pass a feature arg')
  if (!plan) throw new Error('must pass a plan arg')
  if (count === 'undefined') throw new Error('must pass a count arg')
  if (typeof feature !== 'string') throw new Error('feature must be a string')
  if (typeof plan !== 'string') throw new Error('plan must be a string')
  if (typeof count !== 'number') throw new Error('count must be a number')

  // only restrict features for MTU workspaces
  if (isMtuFreePlan(plan) || isMtuTeamPlan(plan) || isMtuBusinessPlan(plan)) {
    // first confirm that we have the plan & feature listed in featureCounts
    if (!featureCounts[plan]) throw new Error('plan does not exist in featureCounts')
    if (featureCounts[plan][feature] === 'undefined') throw new Error('feature does not exist in featureCounts.plan')
    return count < featureCounts[plan][feature]
  }

  // all other plans receive no count limitations for this feature
  return true
}

/**
 * Check if MTU billing plan is a free plan
 */

export function isMtuFreePlan (plan) {
  if (!plan) throw new Error('must pass a plan arg')
  if (typeof plan !== 'string') throw new Error('plan must be a string')

  return plan === 'developer'
}

/**
 * Check if MTU billing plan is a team plan
 */

export function isMtuTeamPlan (plan) {
  if (!plan) throw new Error('must pass a plan arg')
  if (typeof plan !== 'string') throw new Error('plan must be a string')

  return plan === 'team-$100-1-month' || plan === 'team-$0'
}

/**
 * Check if MTU billing plan is a business plan
 */

export function isMtuBusinessPlan (plan) {
  if (!plan) throw new Error('must pass a plan arg')
  if (typeof plan !== 'string') throw new Error('plan must be a string')

  return plan === 'business'
}



// WEBPACK FOOTER //
// ./lib/utils/billing-plans.js